import { useRef, useState } from "react";
import EmailInputForm from "../../../../Components/EmailInputForm";
import PhoneNumberInput from "../../../../Components/PhoneNumberInput";
import TextInput from "../../../../Components/TextInput";
import { Country } from "country-state-city";

const countries = Country.getAllCountries();

function InternalCreateUserModal({ open, onClose, onSubmit, formState, editUser, onEditSubmit }) {
    const isEditMode = !!editUser;
    const [scopes, setScopes] = useState(
        isEditMode ? (editUser.scopes || []) : (formState.scopes || [])
    )
    const [showCountryDropdown, setShowCountryDropdown] = useState(false)
    const firstNameRef = useRef()
    const lastNameRef = useRef()
    const emailRef = useRef()
    const phoneRef = useRef()
    const [isEditingPhone, setIsEditingPhone] = useState(false)
    const [selectedCountries, setSelectedCountries] = useState(
        isEditMode
            ? (editUser?.assignedCountries
                ? countries.filter(c => editUser.assignedCountries.includes(c.name))
                : [])
            : (formState.countries || [])
    )

    if (!open) return null;
    const handleScopeChange = (scopeValue) => {
        setScopes(prev =>
            prev.includes(scopeValue)
                ? prev.filter(s => s !== scopeValue)
                : [...prev, scopeValue]
        )
    }

    const handleCountrySelect = (e) => {
        const isoCode = e.target.value
        if (!isoCode) return
        const country = countries.find(c => c.isoCode === isoCode)
        if (!country) return
        const alreadyAdded = selectedCountries.find(c => c.isoCode === isoCode)
        if (alreadyAdded) return
        setSelectedCountries(prev => [...prev, country])
        e.target.value = ""
    };

    const handleCountryRemove = (isoCode) => {
        setSelectedCountries(prev => prev.filter(c => c.isoCode !== isoCode))

    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const givenName = firstNameRef.current?.validateAndGetData?.()?.value || '';
        const familyName = lastNameRef.current?.validateAndGetData?.()?.value || '';
        const email = emailRef.current?.validateAndGetData?.()?.value || '';

        const phoneResponse = phoneRef.current?.validateAndGetData?.();

        let phoneNumber = undefined;

        if (phoneResponse?.isValid) {
            const { countryCode, number } = phoneResponse.value;

            if (number && countryCode) {
                phoneNumber = `+${countryCode}${number}`
            }
        }

        const assignedCountries = selectedCountries.map(c => c.name).filter(Boolean)
        if (isEditMode) {
            const payload = {
                userId: editUser._id,
                givenName,
                familyName,
                phoneNumber: phoneNumber || editUser.phone_number,
                scopes,
                assignedCountries,
            }
            onEditSubmit(payload);
        } else {
            onSubmit({
                givenName,
                familyName,
                email,
                phoneNumber,
                scopes,
                assignedCountries,
            })
        }
    }
    console.log("Edit data:", {
        editUser
    });
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2 sm:p-4">

            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="p-4 border-b">
                    <h2 className="text-lg sm:text-xl font-semibold">
                        {isEditMode ? "Update Internal User" : "Create Internal User"}
                    </h2>
                </div>

                <div className="overflow-y-auto p-4 flex-1">
                    <form id="internal-user-form" onSubmit={handleSubmit} className="space-y-4">
                        <TextInput title="First Name" data={isEditMode ? editUser.given_name : formState.given_name} ref={firstNameRef} isMandatory={true} />
                        <TextInput title="Last Name" data={isEditMode ? editUser.family_name : formState.family_name} ref={lastNameRef} isMandatory={true} />
                        <EmailInputForm data={isEditMode ? editUser.email : formState.email} title="Internal User Email" isMandatory={true} ref={emailRef} readOnly={isEditMode} />
                        <div>
                            {isEditMode && !isEditingPhone && editUser?.phone_number ? (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="font-semibold text-sm">
                                            Internal User Phone Number
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setIsEditingPhone(true)}
                                            className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                                        >
                                            Update
                                        </button>
                                    </div>
                                    <TextInput
                                        data={editUser.phone_number}
                                        ref={phoneRef}
                                        isMandatory={true}
                                        readOnly
                                    />
                                </div>
                            ) : (
                                <PhoneNumberInput
                                    data={isEditMode ? editUser?.phone_number : formState.phone_number}
                                    title="Internal User Phone Number"
                                    isMandatory={false}
                                    readOnly={false}
                                    ref={phoneRef}
                                />
                            )}
                        </div>
                        <div>
                            <label className="block font-semibold mb-2">User Scope</label>
                            <div className="flex gap-6">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="scope"
                                        value="admin"
                                        checked={scopes.includes("admin")}
                                        onChange={() => handleScopeChange("admin")}
                                    />
                                    Admin
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="scope"
                                        value="qa"
                                        checked={scopes.includes("qa")}
                                        onChange={() => handleScopeChange("qa")}
                                    />
                                    QA
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="scope"
                                        value="executive"
                                        checked={scopes.includes("executive")}
                                        onChange={() => handleScopeChange("executive")}
                                    />
                                    Executive
                                </label>
                                <label className="flex items-center gap-2" title="Read-only access to client invoices only">
                                    <input
                                        type="checkbox"
                                        name="scope"
                                        value="finance"
                                        checked={scopes.includes("finance")}
                                        onChange={() => handleScopeChange("finance")}
                                    />
                                    Finance
                                </label>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700">
                                    Client Location Handled
                                </label>
                                {isEditMode && (
                                    <button
                                        type="button"
                                        onClick={() => setShowCountryDropdown(prev => !prev)}
                                        className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                                    >
                                        {showCountryDropdown ? "Close" : "Add / Remove Country"}
                                    </button>
                                )}
                            </div>
                            {(!isEditMode || showCountryDropdown) && (
                                <select
                                    className="border border-gray-300 rounded-md p-2 bg-white w-full focus:ring-blue-500 focus:border-blue-500 mb-2"
                                    defaultValue=""
                                    onChange={handleCountrySelect}
                                >
                                    <option value="">Select Country</option>
                                    {countries.map(country => (
                                        <option key={country.isoCode} value={country.isoCode}>
                                            {country.flag} {country.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {selectedCountries.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {selectedCountries.map(country => (
                                        <div
                                            key={country.isoCode}
                                            className="flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-800 font-medium"
                                        >
                                            <span>{country.flag} {country.name}</span>
                                            {(!isEditMode || showCountryDropdown) && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleCountryRemove(country.isoCode)}
                                                    className="ml-1 text-blue-400 hover:text-blue-700 font-bold leading-none"
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </form>
                </div>
                <div className="p-2 border-t flex justify-end gap-2 bg-white">
                    <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={onClose}>
                        Cancel
                    </button>

                    <button form="internal-user-form" type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                        {isEditMode ? "Update" : "Submit"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default InternalCreateUserModal;

